import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject, combineLatest, filter, of } from 'rxjs';
import { map, pairwise, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import {
  selectNraIndicator,
  selectRegistrationType,
  selectStateCode,
  selectStateTaxRate,
  selectTemplatePrefilledFlowStarted,
} from '../../../account-info-header/reducers';
import { TransactionFormActions } from '../../../create-request/actions';
import {
  OptionDropdown,
  SelectControl,
} from '../../../create-request/models/OptionDropdownSelect';
import { TaxWithholdingFields, TransactionFormServiceError } from '../../../create-request/models/form/TransactionForm';
import { AchTransactionDetailsForm, CheckTransactionDetailsForm, DistributionReasonValues, ItTransactionDetailsForm, TaxWithholdingForm, WireTransactionDetailsForm } from '../../../create-request/models/form/TransactionFormGroup';
import { DistributionReason } from '../../../create-request/models/internal-transfer/DistributionReason';
import { InternalTransferInfo } from '../../../create-request/models/internal-transfer/InternalTransferInfo';
import {
  selectAchStwProcessCode,
  selectCheckInternationalFedTaxError,
  selectCheckIsRmdEligible,
  selectCheckStwProcessCode,
  selectDefaultAchTaxWithholding,
  selectFedTaxPercentDefault,
  selectFrequency,
  selectIsBundledOneTimeTotalDist,
  selectIsFromEdit,
  selectIsFromEditSubmittedTransaction,
  selectIsRecurring,
  selectItIsRmdEligible,
  selectItStwProcessCode,
  selectPayeeType,
  selectStateTaxPercentDefault,
  selectStateTypeInd,
  selectTransactionFormServiceError,
  selectTransactionFormTransactionDetails,
  selectTransactionType,
  selectUseStandingAuth,
  selectWireInternationalFedTaxError,
  selectInternalTransferInternationalFedTaxError
} from '../../../create-request/reducers';
import { buildFromPrefilledForm } from '../../../create-request/utilities/formUtilities';
import { selectTaxWithholding } from '../../../transaction-view/reducers';
import { FormatPercentPipe } from '../../pipes/FormatPercentPipe';
import { FeatureFlagService } from '../../services/feature-flag-service';
import { ValidateInputService } from '../../services/validate-input.service';
import { Hyperlink } from '../../types/Hyperlink';
import { TransactionType } from '../../types/Transaction';
import {
  Constants,
  RothConversion,
  RothWarnings,
  amountTypeDict,
  distributionReasons,
  fedTaxErrorMap,
  registrationTypes,
  stateCodes,
  stateTaxErrorMap,
  transactionDetailLabels,
  transactionTypes,
} from '../../utilities/Constants';
import * as CustomValidators from '../../utilities/FormValidators';
import { percentFormatter } from '../../utilities/PercentFormatter';
import { numberOrDecimalOnly, percentAllowedChars } from '../../utilities/regex-patterns';

import { selectIsEnableSpecialStateTax } from '../../../feature-flags/reducers';

@Component({
  standalone: false,
  selector: 'mm-create-request-tax-withholdings',
  templateUrl: './create-request-tax-withholdings.component.html',
  styleUrls: ['./create-request-tax-withholdings.component.scss'],
})
export class CreateRequestTaxWithholdingsComponent
  implements OnInit, OnDestroy {
  @Input() distributionReason$: Observable<DistributionReason>;
  @Input() distributionReasonMap$: BehaviorSubject<DistributionReason[]>;
  @Input() distributionReasonTooltip: string = '';
  @Input() distributionReasonTooltipHtml: string = '';
  @Input() showY4CodeStandingAuth: boolean = false;
  @Input() parentForm: FormGroup<CheckTransactionDetailsForm | WireTransactionDetailsForm | ItTransactionDetailsForm | AchTransactionDetailsForm>;
  @Input() taxWithholdings: TaxWithholdingFields;
  @Input() taxWithholdingsFormGroupDetails: TaxWithholdingFields | undefined;
  @Input() editAchProfile: boolean = false;
  @Input() isReviewEdit: boolean = false;
  @Input() isPositionsTransaction: boolean = false;
  @Output() showCashieringIRAAgreementModal = new EventEmitter<boolean>();
  @Input() frequency$: Observable<"recurring" | "onetime" | "onetimeTotalDist" | undefined> = of("onetime");
  destroy$: Subject<boolean> = new Subject<boolean>();
  distributionReasonModel: SelectControl = {
    title: 'Distribution reason',
    controlName: 'distributionReason',
    options: [],
    validationMessage: 'Distribution reason needs to be selected',
  };
  formatPercent = new FormatPercentPipe;
  distributionReasonModel$: Observable<SelectControl> | SelectControl;
  distributionReasonOption: string;
  isRecurringTransaction: boolean;
  itInfo: InternalTransferInfo;
  nraIndicator: boolean;
  nraText: string = Constants.nraText;
  percentAllowedChars = percentAllowedChars;
  rmdAlertText: string = Constants.rmdAlertText;
  qcdDistributionInformation: string = Constants.qcdDistributionInformation;
  regType: string;
  stateCode: string;
  stateTypeIndicator: string;
  accountStateTaxRate: string;
  defaultStateTax: string;
  defaultFedTax: string;
  stwProcessCode: string;
  taxWithholdingText: string = Constants.taxWithholdingText;
  transactionType: TransactionType | '';
  transactionTypes: transactionTypes;
  isOneTime: boolean = false;
  useStandingAuth: boolean;
  is100TweEligible: boolean = false;
  disableStateTax: boolean;
  isRmdEligible: boolean;
  oneTimeCheck100PercentTWEInfo: string =
    Constants.oneTimeCheck100PercentTWEInfo;
  warningText: string;
  isFromEdit: boolean;
  distributionReasonCode: string | undefined;
  changingFromDistQ: boolean;
  previousDistributionReason: string;
  isFromEditSubmittedTransaction: boolean | undefined;
  isCreateRecurringAchFromOnRequest$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isCreateRecurringAch: boolean
  readonly formControlName: string = 'distributionReason';
  transactionFormServiceError$: Observable<TransactionFormServiceError>;
  isAchTweAuthorizationError: boolean;
  achTWEAuthorizationErrorText: string = Constants.achCTWEAuthorizationError;
  achTWErrorLink: Hyperlink = {
    url: '',
    text: 'create a new profile',
    target: '_blank',
    isActionCenterHyperLink: true
  }
  showTweCalculatorModal = new EventEmitter<boolean>();
  isFedTax$ = new BehaviorSubject<boolean>(false);
  enableInterimIsoWires: boolean = false;
  stateTaxLabel: string = transactionDetailLabels.stateTax;
  isOnetimeTotalDistChecks: boolean;
  isBundledOnetimeTotalDistChecks: boolean;
  hasInternationalFedTaxError: boolean = false;
  internationalFedTaxErrorText = fedTaxErrorMap.internationalFedTaxError;
  isSpecialStateTaxFFEnabled: boolean;
  hasOnlyStateTaxError: boolean = false;
  
  constructor(
    public validationService: ValidateInputService,
    private fb: FormBuilder,
    private store: Store,
    private featureFlagService: FeatureFlagService
  ) { }

  ngOnInit(): void {
    this.store.select(selectIsFromEditSubmittedTransaction)
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => this.isFromEditSubmittedTransaction = val);

    this.store
      .select(selectTransactionType)
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => (this.transactionType = val));

    this.store
      .select(selectIsRecurring)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isRecurring) => {
        this.isRecurringTransaction = isRecurring;
      });

    combineLatest([
      this.store.select(selectItIsRmdEligible),
      this.store.select(selectCheckIsRmdEligible),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([iTisRmdEligible, checkIsRmdEligible]) => {
        this.isRmdEligible = iTisRmdEligible || checkIsRmdEligible;
      });

    if (this.distributionReason$) {
      this.distributionReason$
        .pipe(takeUntil(this.destroy$))
        .subscribe((distReason) => {
          this.distributionReasonOption = distReason?.description;
          this.updateFieldsOnDistributionReason(distReason?.description);
        });
    } else {
      this.distributionReasonOption = this.taxWithholdings?.distributionReason;
    }

    this.store
      .select(selectUseStandingAuth)
      .pipe(takeUntil(this.destroy$))
      .subscribe((useStandingAuth) => (this.useStandingAuth = useStandingAuth));

    combineLatest([
      this.store.select(selectCheckInternationalFedTaxError),
      this.store.select(selectWireInternationalFedTaxError),
      this.store.select(selectInternalTransferInternationalFedTaxError)
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([checkInternationalFedTaxError, wireInternationalFedTaxError, internalTransferFedTaxError]) => {
        const hasError = checkInternationalFedTaxError || wireInternationalFedTaxError || internalTransferFedTaxError;
        if(hasError) {
          this.fedTaxPercent?.setErrors({ 'internationalFedTaxError': true });
          this.hasInternationalFedTaxError = true;
        } else {
          this.hasInternationalFedTaxError = false;
        }
      });

    this.store
      .select(selectFrequency)
      .pipe(takeUntil(this.destroy$))
      .subscribe((frequency) => {
        this.isOneTime = frequency === 'onetime';
        this.isCreateRecurringAchFromOnRequest$.next(this.isAchTransaction && frequency === 'recurring');
      });

    this.store.select(selectFrequency).pipe(
      takeUntil(this.destroy$),
      withLatestFrom(
        this.store.select(selectDefaultAchTaxWithholding)
      )
    ).subscribe(([_frequency, defaultTaxWithholding]) => {
      if (this.transactionType === transactionTypes.ach) {
        this.distributionReason?.setValue(defaultTaxWithholding.distributionReason);
        if (defaultTaxWithholding.distributionReason !== 'QUALIFIED (Q)') {
          this.taxWithholdings.fedTaxPercent = defaultTaxWithholding.fedTaxPercent;
          this.taxWithholdings.stateTaxPercent = defaultTaxWithholding.stateTaxPercent;

          if(!this.updateTaxWithHoldingOnEdit){
            this.fedTaxPercent?.setValue(this.fedTaxFormatted);
            this.stateTaxPercent?.setValue(this.stateTaxFormatted);
          }
        }
      }
    })

    this.is100TweEligible =
      this.transactionType === transactionTypes.check && this.isOneTime;

    if (this.isItTransaction) {
      this.store
        .select(selectItStwProcessCode)
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          this.stwProcessCode = value;
        });
    } else if (this.isCheckTransaction) {
      this.store
        .select(selectCheckStwProcessCode)
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          this.stwProcessCode = value;
        });
    } else if (this.isAchTransaction) {
      this.store
        .select(selectAchStwProcessCode)
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          this.stwProcessCode = value;
        });
    }

    combineLatest([
    this.store.select(selectStateCode),
    this.store.select(selectTaxWithholding)
  ]).pipe(takeUntil(this.destroy$))
    .subscribe(([stateCode, taxWithholding]) => {
      if (this.isFromEditSubmittedTransaction) {
        this.stateCode = taxWithholding.stateCode ?? '';
      } else {
        this.stateCode = stateCode;
      }
    });

    const stateTaxValidators = [
      Validators.required,
      CustomValidators.zeroOrGreater,
      CustomValidators.isANumber,
      this.is100TweEligible
        ? CustomValidators.oneTimeCheckGreaterThan99State
        : CustomValidators.greaterThan99State,
      Validators.maxLength(10),
    ];

    this.store.select(selectIsEnableSpecialStateTax).pipe(takeUntil(this.destroy$)).subscribe((isEnabled) => {
      this.isSpecialStateTaxFFEnabled = isEnabled;
      if (!isEnabled){
        stateTaxValidators.splice(
          stateTaxValidators.length - 1,
          0,
          CustomValidators.belowMIStateTax(
            this.stateTaxPercent,
            this.stateCode,
            this.taxWithholdings.stateTaxPercent
          ),
          CustomValidators.belowMNStateTax(
            this.stateTaxPercent,
            this.stateCode,
            this.taxWithholdings.stateTaxPercent
          )
      );
      }
    })

    const taxWithholdingsFormGroup: FormGroup<TaxWithholdingForm> = this.fb.nonNullable.group<TaxWithholdingForm>({
      distributionReason: this.fb.nonNullable.control(this.distributionReasonFormatted, [Validators.required]),
      fedTaxPercent: this.fb.nonNullable.control(this.fedTaxFormatted, [
        Validators.required,
        CustomValidators.zeroOrGreater,
        CustomValidators.wholeNumber,
        this.is100TweEligible
          ? CustomValidators.oneTimeCheckGreaterThan100
          : CustomValidators.greaterThan99FedRequestCheck,
        Validators.maxLength(10),
      ]),
      stateTaxPercent: this.fb.nonNullable.control(this.stateTaxFormatted, stateTaxValidators),
    });

    this.parentForm.setControl(
      'taxWithholdingsFormGroup',
      taxWithholdingsFormGroup
    );

    if (this.is100TweEligible) {
      this.taxWithholdingsFormGroup.setValidators(
        CustomValidators.sumDoesNotExceed100('fedTaxPercent', 'stateTaxPercent')
      );
    } else {
      this.taxWithholdingsFormGroup.setValidators(
        CustomValidators.sumDoesNotExceed99Point99(
          'fedTaxPercent',
          'stateTaxPercent'
        )
      );
    }

    this.store
      .select(selectTemplatePrefilledFlowStarted)
      .pipe(
        withLatestFrom(
          this.store.select(selectTransactionFormTransactionDetails),
        ),
        filter(([started, _transactionDetails]) => started),
        takeUntil(this.destroy$)
      )
      .subscribe(([_, transactionDetails]) => {
        buildFromPrefilledForm(
          transactionDetails.taxWithholdingsFormGroup,
          this.taxWithholdingsFormGroup
        );
        this.store.dispatch(TransactionFormActions.templatePrefilledFlowComplete());
      });

 this.store
      .select(selectIsFromEdit)
      .pipe(
        takeUntil(this.destroy$),
      ).subscribe((isFromEdit) => {
        this.isFromEdit = isFromEdit;
        if (isFromEdit && !this.isReviewEdit && this.taxWithholdingsFormGroupDetails) {
          this.distributionReason.setValue(
            this.taxWithholdingsFormGroupDetails.distributionReason
          );
          this.fedTaxPercent?.setValue(
            this.taxWithholdingsFormGroupDetails.fedTaxPercent
          );
          this.stateTaxPercent?.setValue(
            this.taxWithholdingsFormGroupDetails.stateTaxPercent
          );
          this.taxWithholdings.fedTaxPercent =
            this.taxWithholdingsFormGroupDetails.fedTaxPercent;
          this.taxWithholdings.stateTaxPercent =
            this.taxWithholdingsFormGroupDetails.stateTaxPercent;
          this.taxWithholdings.distributionReason =
            this.taxWithholdingsFormGroupDetails.distributionReason;

          if (this.isTaxExempt()) {
            this.resetAndLockTaxFields();
          }
        } else if (isFromEdit && !this.isReviewEdit && !this.taxWithholdingsFormGroupDetails) {
          this.distributionReason.setValue(this.distributionReasonOption);
          if (this.isTaxExempt()) {
            this.resetAndLockTaxFields();
          }
        }
        //Dispatching editLoadComplete after the patching is done
        setTimeout(() => {
        this.store.dispatch(TransactionFormActions.editLoadComplete());
        }, 0);
      });

    this.store
      .select(selectRegistrationType)
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        this.regType = val;
      });

    this.isCreateRecurringAchFromOnRequest$.subscribe(isCreateRecurringAch => {
      if (this.isAchTransaction) {
        if (isCreateRecurringAch) {
          this.setTaxFields(false, !this.nraIndicator);
          this.distributionReason.enable();
          this.distributionReason.setValue(
            this.taxWithholdings.distributionReason
          );
        } else if (this.taxWithholdings.distributionReason === 'QUALIFIED (Q)') {
          this.resetAndLockTaxFields();
          this.distributionReason.disable();
          this.distributionReason.setValue(
            this.taxWithholdings.distributionReason
          );
        } else {
          this.setTaxFields(false, true);
          this.distributionReason.disable();
          this.distributionReason.setValue(
            this.taxWithholdings.distributionReason
          );
        }
      }
      this.isCreateRecurringAch = isCreateRecurringAch;
    });

    this.isCreateRecurringAchFromOnRequest$.subscribe(isCreateRecurringAch => {
    if (this.distributionReasonMap$) {
      this.distributionReasonModel$ = this.distributionReasonMap$.pipe(
        map((distReasonMap) => {
          this.taxWithholdings.distributionReasonMap = distReasonMap;
          return {
            title: 'Distribution reason',
            controlName: 'distributionReason',
            options: this.mapDistributionReasons(distReasonMap),
            validationMessage: 'Distribution reason needs to be selected',
          };
        })
      );
    } else {
      this.distributionReasonModel$ = {
        title: 'Distribution reason',
        controlName: 'distributionReason',
        options: this.mapDistributionReasons(
          this.taxWithholdings?.distributionReasonMap || []
        ),
        validationMessage: 'Distribution reason needs to be selected',
      };
    }
    });

    this.distributionReason.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        if (this.taxWithholdings.distributionReasonMap) {
          const selectedReason =
            this.taxWithholdings.distributionReasonMap.filter(
              (element) => element.description === val
            )[0] ?? ({} as DistributionReason);
          const distributionReason: DistributionReason = {
            ...selectedReason,
            code: selectedReason.code?.trim()
          };
          this.store.dispatch(
            TransactionFormActions.distributionReasonChanged({
              distributionReason: distributionReason,
              transactionType: this.transactionType,
            })
          );
          this.updateFieldsOnDistributionReason(val);
        }
      });

    this.store
      .select(selectNraIndicator)
      .pipe(takeUntil(this.destroy$))
      .subscribe((nraIndicator) => {
        this.nraIndicator = nraIndicator ?? false;
        if (nraIndicator) {
          this.resetAndLockTaxFields();
        }
      });

    this.disableStateTaxFormControl();

    combineLatest([
      this.store.select(selectStateTypeInd),
      this.store.select(selectTaxWithholding)
    ]).pipe(takeUntil(this.destroy$))
      .subscribe(([stateTypeInd, taxWithholding]) => {
        if (this.isFromEditSubmittedTransaction) {
          this.stateTypeIndicator = taxWithholding.stateTypeIndicator ?? '';
        } else {
          this.stateTypeIndicator = stateTypeInd;
        }
      });

    this.store
      .select(selectStateTaxRate)
      .pipe(takeUntil(this.destroy$))
      .subscribe((stateTaxRate) => {
        this.accountStateTaxRate = stateTaxRate;
      });

    combineLatest([
      this.store.select(selectStateTaxPercentDefault),
      this.store.select(selectTaxWithholding)
    ]).pipe(takeUntil(this.destroy$))
      .subscribe(([defaultStateTax, taxWithholding]) => {
        if (this.isFromEditSubmittedTransaction) {
          this.defaultStateTax = taxWithholding.defaultStateTax ?? '';
        } else {
          this.defaultStateTax = defaultStateTax;
        }
      });

    this.store.select(selectFedTaxPercentDefault)
      .pipe(takeUntil(this.destroy$))
      .subscribe((fedDefault) => {
        this.defaultFedTax = fedDefault;
      })

    if (this.isFromEditSubmittedTransaction && this.isTaxExempt()) {
      this.fedTaxPercent?.disable();
      this.stateTaxPercent?.disable();
    }

    this.transactionFormServiceError$ = this.store.select(selectTransactionFormServiceError);
    this.transactionFormServiceError$.pipe(takeUntil(this.destroy$)).subscribe(val => {
      if (val.url.includes("MoneyLinkVerify") && (val.status.toString().startsWith('4')) && val.errorList) {
        this.isAchTweAuthorizationError = val.errorList?.map(err => err.detail).toString().startsWith(Constants.achCTWEShortenedResponseText) ? true : false;
      } else {
        this.isAchTweAuthorizationError = false;
      }
    });

    this.store.select(selectStateCode).pipe(takeUntil(this.destroy$)).subscribe((stateCode) => {
      if (stateCode && !this.nraIndicator) {
        this.stateTaxLabel = `${transactionDetailLabels.stateTax} (${stateCode})`;
      }
      if (this.featureFlagService.featureFlags.enableSpecialStateTax && stateCode?.toUpperCase() === 'MS') {
        this.taxWithholdingsFormGroup.addValidators(
          CustomValidators.requiresStateTaxForEarlyDistribution
        );
      }
    });

    this.frequency$.pipe(take(1)).subscribe(frequency => {
      this.isOnetimeTotalDistChecks = frequency === 'onetimeTotalDist' && this.transactionType === transactionTypes.check;
      if(this.isOnetimeTotalDistChecks) {
        this.fedTaxPercent?.disable();
        this.stateTaxPercent?.disable();
        this.distributionReason?.disable();
      }
    })

    this.store.select(selectIsBundledOneTimeTotalDist).pipe(takeUntil(this.destroy$)).subscribe((isBundledOneTimeTotalDist) => {
      this.isBundledOnetimeTotalDistChecks = isBundledOneTimeTotalDist;
      if (this.isBundledOnetimeTotalDistChecks) {
        this.fedTaxPercent?.disable();
        this.stateTaxPercent?.disable();
        this.distributionReason?.disable();
      }
    });    
  }

  mapDistributionReasons(
    distributionReasonList: DistributionReason[]
  ): OptionDropdown[] {
    if (this.isItTransaction || this.isCheckTransaction) {
      if (this.distributionReasonOption) {
        this.distributionReason?.setValue(this.distributionReasonOption);
        if (this.useStandingAuth) {
          if (this.isCheckTransaction) {
            if (this.showY4CodeStandingAuth) {
              const index = distributionReasonList?.findIndex(distReasons => distReasons.code.trim() === distributionReasons.qcdY4Code);
              if (index !== -1) {
                this.distributionReason?.setValue(distributionReasons.qcdY4);
              }
            }
            this.distributionReason?.disable();
          }
          this.updateFieldsOnDistributionReason(this.distributionReasonOption);
          if (
            !this.isFromEdit &&
            this.isRmdEligible &&
            this.distributionReasonOption == 'NORMAL (7)'
          ) {
            this.amount?.setValue('');
          }
        }
      }
    }
    if (this.isCheckTransaction) { //Reset distributionReason selection when payeeType is changed
      this.store.select(selectPayeeType)
        .pipe(
          takeUntil(this.destroy$),
          pairwise(),
        ).subscribe(([previous, current]) => {
        if (previous !== current) {
          this.distributionReason?.setValue('');
        }
      });
    }

    if (this.isAchTransaction) {
      if (this.isRecurringTransaction) {
        return distributionReasonList
        .filter((elem) => elem.description !== 'RMD (7-RMD)')
        .filter((elem) => elem.description !== 'Select One')
        .filter((elem) => elem.description !== distributionReasons.qcd)
        .map(
          (element) =>
            new OptionDropdown(element.description, element.description)
          );
      }
      else {
        return distributionReasonList
        .filter((elem) => elem.description !== 'Select One')
        .filter((elem) => elem.description !== distributionReasons.qcd)
        .map(
          (element) =>
            new OptionDropdown(element.description, element.description)
        );
      }
    }
    else if (this.isItTransaction) {
      return distributionReasonList
      .filter((elem) => elem.description !== 'Select One')
      .filter((elem) => elem.description !== distributionReasons.qcd)
      .map(
        (element) =>
          new OptionDropdown(element.description, element.description)
      );
    }
    else if (this.isWireTransaction) {
      return distributionReasonList
        .filter((elem) => elem.description !== distributionReasons.qcdY4) 
        .filter((elem) => elem.description !== distributionReasons.qcd) 
        .filter((elem) => elem.description !== 'Select One')       
        .map(
          (element) =>
            new OptionDropdown(element.description, element.description)
        );
    }
    else {
      return distributionReasonList
      .filter((elem) => elem.description !== 'Select One')
      .map(
        (element) =>
          new OptionDropdown(element.description, element.description)
      );
    }
  }

  ngOnDestroy(): void {
    this.parentForm?.removeControl('taxWithholdingsFormGroup');

    this.isCreateRecurringAchFromOnRequest$.complete();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  get taxWithholdingsFormGroup() {
    return this.parentForm.controls
      .taxWithholdingsFormGroup as FormGroup<TaxWithholdingForm>;
  }

  get distributionReason() {
    return this.taxWithholdingsFormGroup?.controls?.distributionReason;
  }

  get isQcdDistributionReason() {
    return this.distributionReason.value === distributionReasons.qcd;
  }

  get isQcdY4DistributionReason() {
    return this.distributionReason.value === distributionReasons.qcdY4;
  }

  get distributionReasonFormatted() {
    return this.taxWithholdings?.distributionReason ? this.taxWithholdings.distributionReason : '';
  }

  get distributionReasonControl(): FormControl<typeof DistributionReasonValues[number]>{
    return (this.taxWithholdingsFormGroup?.get(this.formControlName)) as FormControl<typeof DistributionReasonValues[number]>;
  }

  get fedTaxPercent() {
    return this.taxWithholdingsFormGroup?.controls?.fedTaxPercent;
  }

  get stateTaxPercent() {
    return this.taxWithholdingsFormGroup?.controls?.stateTaxPercent;
  }

  get amountType() {
    return this.parentForm.controls?.amountType;
  }

  get amount() {
    return this.parentForm.controls?.amount;
  }
  get fedTaxFormatted() {
    return this.formatPercent.transform(this.taxWithholdings?.fedTaxPercent, 3);
  }

  get stateTaxFormatted() {
    return this.formatPercent.transform(this.taxWithholdings?.stateTaxPercent, 3);
  }

  get updateTaxWithHoldingOnEdit(): boolean {
    if (this.isReviewEdit && this.transactionType === transactionTypes.ach) {
      this.fedTaxPercent?.setValue(this.taxWithholdingsFormGroupDetails?.fedTaxPercent ?? ''),
        this.stateTaxPercent?.setValue(this.taxWithholdingsFormGroupDetails?.stateTaxPercent ?? '')
      return true;
    }
    return false;
  }

  updateFieldsOnDistributionReason(distReason: string) {
    if((this.isOnetimeTotalDistChecks || this.isBundledOnetimeTotalDistChecks) && !this.useStandingAuth) return;
    if (this.isTaxExempt() || this.isEducationalSavings()) {
      this.resetAndLockTaxFields();
    } else if (!this.nraIndicator) {
      if (this.fedTaxPercent?.disabled || 
          ((this.previousDistributionReason === distributionReasons.qcd || this.previousDistributionReason === distributionReasons.qcdY4 || this.previousDistributionReason === distributionReasons.qualified) && distReason !== distributionReasons.qcd && distReason !== distributionReasons.qcdY4 && distReason !== distributionReasons.qualified)) {
        let enableTaxFields = !(this.isAchTransaction && this.isOneTime);
        let useDefaultValues = this.previousDistributionReason === distributionReasons.qcd || this.previousDistributionReason === distributionReasons.qcdY4 || 
          this.previousDistributionReason === distributionReasons.qualified;
        this.setTaxFields(useDefaultValues, enableTaxFields);
      }
    }
    this.previousDistributionReason = distReason;
  }

  get isFedTaxInvalid(): boolean {
    return (
      (this.fedTaxPercent?.touched && this.fedTaxPercent?.invalid) ||
      this.fedAndStateTaxSumError !== ''
    );
  }

  get isStateTaxInvalid(): boolean {
    return (
      (this.stateTaxPercent?.touched && this.stateTaxPercent?.invalid) ||
      this.fedAndStateTaxSumError !== ''
    );
  }

  get fedTaxError() {
    if (this.fedTaxPercent && this.fedTaxPercent.errors) {
      const errorKeys: string[] = Object.keys(this.fedTaxPercent.errors ?? {});
      if (errorKeys.length > 0) {
        const filteredErrorKeys = this.hasInternationalFedTaxError 
          ? errorKeys.filter(key => key !== 'internationalFedTaxError')
          : errorKeys;
        
        if (filteredErrorKeys.length > 0) {
          return fedTaxErrorMap[filteredErrorKeys[0] as keyof typeof fedTaxErrorMap];
        }
      }
    }
  }

  get stateTaxError() {
    if ((this.stateTaxPercent && this.stateTaxPercent.errors) || this.hasMississippiStateTaxErrorText) {
      var stateTaxErrorMS = this.featureFlagService.featureFlags.enableSpecialStateTax && this.taxWithholdingsFormGroup?.errors?.stateTaxErrorTextMS ? { 'stateTaxErrorTextMS': true } : null;
      const errorKeys: string[] = Object.keys(
        (stateTaxErrorMS ? stateTaxErrorMS : this.stateTaxPercent.errors) ?? {},
      );
      if (errorKeys.length > 0) {
        return stateTaxErrorMap[errorKeys[0] as keyof typeof stateTaxErrorMap];
      }
    }
  }

  get fedAndStateTaxSumError(): string {
    if (this.taxWithholdingsFormGroup && this.taxWithholdingsFormGroup.errors) {
      const errorKeys: string[] = Object.keys(
        this.taxWithholdingsFormGroup.errors ?? {}
      );
      this.hasOnlyStateTaxError = this.featureFlagService.featureFlags.enableSpecialStateTax ? errorKeys.length === 1 && errorKeys.includes('stateTaxErrorTextMS') : false;
      if (errorKeys.length > 0 && this.is100TweEligible && !this.hasOnlyStateTaxError) {
        return 'Update values so total tax withholding does not exceed 100%.';
      } else if (errorKeys.length > 0 && !this.hasOnlyStateTaxError) {
        return 'Total tax withholding cannot exceed 99.99%. Update values or request a one-time check to withhold 100%';
      }
    }
    return '';
  }

  get showRmdBanner() {
    return this.distributionReason.value === distributionReasons.rmd;
  }

  get hasMississippiStateTaxErrorText(): boolean {
    return this.featureFlagService.featureFlags.enableSpecialStateTax && this.stateCode?.toUpperCase() === stateCodes.Mississippi && this.taxWithholdingsFormGroup?.errors?.stateTaxErrorTextMS;
  }

  get showMississippiEarlyDistributionWarning(): boolean {
    return this.featureFlagService.featureFlags.enableSpecialStateTax && this.stateCode?.toUpperCase() === stateCodes.Mississippi && this.distributionReason?.value?.toUpperCase() === distributionReasons.early;
  }
  
  get showMassachusettsDistributionWarning(): boolean {
    return this.featureFlagService.featureFlags.enableSpecialStateTax && this.stateCode?.toUpperCase() === stateCodes.Massachusetts;
  }

  get stateTaxAttentionRequiredWarningMessage() {
    if (this.stateCode?.toUpperCase() === stateCodes.Massachusetts) {
      return Constants.retirementStateTaxAttentionRequiredTextMA;
    }
    else if (this.stateCode?.toUpperCase() === stateCodes.Mississippi) {
      return Constants.retirementStateTaxAttentionRequiredTextMS;
    } else {
      return '';
    }
  }

  get isAchTransaction() {
    return this.transactionType === transactionTypes.ach;
  }

  get isWireTransaction() {
    return this.transactionType === transactionTypes.wire;
  }

  get isCheckTransaction() {
    return this.transactionType === transactionTypes.check;
  }

  get isItTransaction() {
    return this.transactionType === transactionTypes.internalTransfer;
  }

  get recurringFieldsFormGroup() {
    return (this.parentForm.controls as ItTransactionDetailsForm)['recurringFieldsFormGroup']?.value;
  }

  disableStateTaxFormControl(): void {
    if (!(this.nraIndicator || this.isEducationalSavings() || (this.stwProcessCode && this.stwProcessCode !== 'N') || this.taxWithholdings.stateHasTaxes)) {
      this.stateTaxPercent.disable();
      this.disableStateTax = true;
    }
  }

  onFedTaxBlur(): void {
    if (this.fedTaxPercent.valid) {
      this.fedTaxPercent?.setValue(
        this.formatPercent.transform(this.fedTaxPercent.value, 3)
      );
    }
  }

  onStateTaxBlur(): void {
    if (this.stateTaxPercent?.valid) {
      this.stateTaxPercent?.setValue(
        this.formatPercent.transform(this.stateTaxPercent.value, 3)
      );
    }
  }

  resetAndLockTaxFields(): void {
    if (!this.updateTaxWithHoldingOnEdit) {
      this.fedTaxPercent?.setValue(Constants.zeroPercent);
      this.stateTaxPercent?.setValue(Constants.zeroPercent);
    }
    this.fedTaxPercent?.disable();
    this.stateTaxPercent?.disable();
  }

  setTaxFields(useDefaultValues: boolean, isEnabled: boolean): void {
    if(this.isOnetimeTotalDistChecks || this.isBundledOnetimeTotalDistChecks) return
    let stateTaxPercent = this.stateTaxFormatted;
    let fedTaxPercent = this.fedTaxFormatted;
    if (useDefaultValues) {
      stateTaxPercent = percentFormatter(this.defaultStateTax);
      fedTaxPercent = percentFormatter(this.defaultFedTax);
    }
    if(!this.updateTaxWithHoldingOnEdit){
    this.fedTaxPercent?.setValue(fedTaxPercent);
    this.stateTaxPercent?.setValue(stateTaxPercent);
    }

    if (isEnabled) {
      this.fedTaxPercent?.enable();
      if (!this.disableStateTax){
        this.stateTaxPercent?.enable();
      }
    } else {
      this.fedTaxPercent?.disable();
      this.stateTaxPercent?.disable();
    }

    if (this.isEducationalSavings()) {
      this.resetAndLockTaxFields();
    }
  }

  isTaxExempt(): boolean {
    let distributionReasonSubCode: string | undefined;
    if (this.isAchTransaction && !this.isCreateRecurringAch) {
      this.distributionReasonCode = this.distributionReason.value?.split(' ')[1][1];
    } else {
      let distributionReason = this.taxWithholdings?.distributionReasonMap?.find(
        (element) => element.description === this.distributionReason?.value
      );
      this.distributionReasonCode = distributionReason?.code;
      distributionReasonSubCode = distributionReason?.subCode;
    }

    if (this.distributionReasonCode) {
      return (
        this.distributionReasonCode.trim() ===
        RothConversion.DistributionReasons.COMBINATION_CONVERSION_CODE ||
        this.distributionReasonCode.trim() ===
        RothConversion.DistributionReasons.QUALIFIED_CODE ||
        distributionReasonSubCode?.trim() ===
        RothConversion.DistributionReasons.QCD_SUB_CODE ||
        (this.distributionReasonCode?.trim() === distributionReasons.qcdY4Code)
      );
    }
    return false;
  }

  get showTaxExemptWarning(): boolean {
    return this.isTaxExempt();
  }

  get showWarning() {
    this.warningText = this.showWarningText(this.distributionReason.value, this.taxWithholdings.distributionReasonMap ?? []);
    if (this.warningText) {
      return true;
    } else {
      return false;
    }
  }

  isQualifiedOrQcdDistribution(selectedReason: DistributionReason[]): boolean {
    const isQualifiedDistribution = selectedReason?.some(
      (element) =>
        element.code.trim() ===
        RothConversion.DistributionReasons.QUALIFIED_CODE
    );

    const isQcdDistribution = selectedReason?.some(
      (element) =>
        element.subCode === RothConversion.DistributionReasons.QCD_SUB_CODE
    );

    const isQcdY4DistributionReason = selectedReason?.some(
      (element) => 
        element.code.trim() === distributionReasons.qcdY4Code
    );

    const is7QcdDistributionReason = selectedReason?.some(
      (element) =>
        element.description === RothConversion.DistributionReasons.QCD_DESCRIPTION
    );

    return isQualifiedDistribution || 
           (isQcdDistribution && !this.hasInternationalFedTaxError) || 
           isQcdY4DistributionReason || 
           (is7QcdDistributionReason && !this.hasInternationalFedTaxError);
  }

  showWarningText(distributionReason: string, distributionReasonList: DistributionReason[]) {
    let selectedReason = distributionReasonList?.filter(
      (element) =>
        element?.description === distributionReason
    );

    let under59SelectionPossible = selectedReason?.some(
      (element) =>
        element.subCode === RothConversion.DistributionReasons.CONVERSION_SUB_CODE &&
        element.code.trim() ===
        RothConversion.DistributionReasons.UNDER_59_AND_A_HALF_CONVERSION_CODE
    );

    let over59SelectionPossible = selectedReason?.some(
      (element) =>
        element.subCode === RothConversion.DistributionReasons.CONVERSION_SUB_CODE &&
        element.code.trim() ===
        RothConversion.DistributionReasons.OVER_59_AND_A_HALF_CONVERSION_CODE
    );

    let CombineAccountPossible = selectedReason?.some(
      (element) =>
        element.subCode === RothConversion.DistributionReasons.COMBINATION_SUB_CODE &&
        element.code.trim() ===
        RothConversion.DistributionReasons.COMBINATION_CONVERSION_CODE
    );

    if (under59SelectionPossible && !this.isRmdEligible) {
      return RothWarnings.under59InputWarning;
    } else if (over59SelectionPossible) {
      return RothWarnings.over59InputWarning;
    } else if (CombineAccountPossible) {
      return RothWarnings.Comb0InputWarning
    } else if (this.isQualifiedOrQcdDistribution(selectedReason)) {
      return RothWarnings.QualifiedDistributionWarning
    } else {
      return '';
    }
  }

  isEducationalSavings(): boolean {
    return this.regType === registrationTypes.educationalSavings;
  }


  get show100TweCheckWarning(): boolean {
    let statePercent = parseFloat(this.stateTaxPercent.value ?? '')
    let totalPercent =
      parseFloat(this.fedTaxPercent.value ?? '') +
      parseFloat(this.stateTaxPercent.value ?? '');
    return totalPercent === 100 && statePercent !== 100 && this.is100TweEligible;
  }

  displayIraAgreementModal() {
    this.showCashieringIRAAgreementModal.emit(true);
  }

  onStateTaxCalculatorClick() {
    this.isFedTax$.next(false);
    this.showTweCalculatorModal.emit(true);
  }

  onFedTaxCalculatorClick() {
    this.isFedTax$.next(true);
    this.showTweCalculatorModal.emit(true);
  }

  setTaxPercentValue(fedTaxPercentage: string, stateTaxPercentage: string) {
    if (fedTaxPercentage !== '' && fedTaxPercentage !== null) {
      let fed = isNaN(Number(fedTaxPercentage)) ? '0%' : fedTaxPercentage + '%';
      this.taxWithholdingsFormGroup.controls.fedTaxPercent.setValue(fed);
    }
    else if (stateTaxPercentage !== '' && stateTaxPercentage !== null) {
      let state = isNaN(Number(stateTaxPercentage)) ? '0%' : stateTaxPercentage + '%';
      this.taxWithholdingsFormGroup.controls.stateTaxPercent.setValue(state);
    }
  }

  onPaste(event: ClipboardEvent) {
    //block non-numeric inputs
    if (event.clipboardData != undefined){
      let pastedInput = event.clipboardData.getData('text/plain');
      if (!numberOrDecimalOnly.test(pastedInput)) {
        event.preventDefault();
      }
    }
  }
}
